/**
 * AssigneeStack — overlapping avatars for a task's assignees with a "+N"
 * overflow chip. Renders nothing when there are no assignees.
 */

import { Avatar } from './Avatar';

type StackSize = 'xs' | 'sm' | 'md';

const OVERFLOW_TEXT: Record<StackSize, string> = {
  xs: 'text-[9px] w-5 h-5',
  sm: 'text-[10px] w-6 h-6',
  md: 'text-xs w-8 h-8',
};

interface Assignee {
  userId: string;
  userName: string;
}

interface AssigneeStackProps {
  assignees: Assignee[];
  max?: number;
  size?: StackSize;
  className?: string;
}

export function AssigneeStack({ assignees, max = 3, size = 'sm', className = '' }: AssigneeStackProps) {
  if (!assignees || assignees.length === 0) return null;

  const shown = assignees.slice(0, max);
  const overflow = assignees.length - shown.length;

  return (
    <div className={`flex items-center -space-x-1.5 ${className}`}>
      {shown.map((a) => (
        <Avatar key={a.userId} name={a.userName} userId={a.userId} size={size} ring />
      ))}
      {overflow > 0 && (
        <div
          className={`${OVERFLOW_TEXT[size]} rounded-full flex items-center justify-center font-bold bg-surface-hover text-theme-secondary ring-2 ring-surface-card shrink-0`}
          title={assignees
            .slice(max)
            .map((a) => a.userName)
            .join(', ')}
        >
          +{overflow}
        </div>
      )}
    </div>
  );
}
